import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, CircularProgress, Link, Typography } from "@mui/material";
import { Link as RouterLink, useParams } from "react-router-dom";
import AnimationTypewriterTitle from "../../../mobile-jigsaw-animation/components/AnimationTypewriterTitle";
import { appendScreenshotLang, useScreenshotTranslation } from "../../../../lib/screenshotI18n";
import { screenshotShellFontSx } from "../../../../lib/screenshotFonts";
import ScreenshotExpiredPage from "../../components/ScreenshotExpiredPage";
import {
    getImageUrl,
    getTemplateGrid,
    loadScreenshotSliding,
} from "../../lib/screenshotApi";
import { isScreenshotSceneExpired } from "../../lib/screenshotAccess";
import { SCREENSHOTS_MAIN_PATH } from "../../routes";
import { phoneShellSx, slidingScreenshotAccent } from "../../styles/screenshotStyles";
import { getAnimationScene } from "../animationScene";
import { animationPath } from "../routes";

/**
 * Final animation scene — full puzzle image with centered typewriter brand title + Play Now.
 */
export default function AnimationBrandPage() {
    const { slug } = useParams();
    const { t, lang } = useScreenshotTranslation();
    const scene = getAnimationScene(slug);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [group, setGroup] = useState(null);
    const [template, setTemplate] = useState(null);
    const [showPlayButton, setShowPlayButton] = useState(false);
    const finishTimerRef = useRef(null);
    const brandTitle = scene?.brandTitle ?? "SLIDING PUZZLE QUEST";
    const typewriterCharMs = scene?.typewriterCharMs ?? 120;
    const typewriterInitialDelayMs = scene?.typewriterInitialDelayMs ?? 400;
    const brandHoldMs = scene?.brandHoldMs ?? 1200;

    useEffect(() => {
        if (!scene) {
            setLoading(false);
            return undefined;
        }

        let cancelled = false;
        (async () => {
            setLoading(true);
            setError(null);
            try {
                const result = await loadScreenshotSliding(scene);
                if (cancelled) return;
                setGroup(result.group);
                setTemplate(result.template);
            } catch (e) {
                if (!cancelled) {
                    setError(e?.message ?? "Failed to load sliding puzzle");
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [scene]);

    const grid = useMemo(() => (template ? getTemplateGrid(template) : 0), [template]);
    const imageUrl = getImageUrl(group?.story);

    const brandDurationMs = useMemo(
        () =>
            typewriterInitialDelayMs +
            brandTitle.length * typewriterCharMs +
            brandHoldMs,
        [typewriterInitialDelayMs, brandTitle.length, typewriterCharMs, brandHoldMs],
    );

    const typewriterCompleteMs = useMemo(
        () => typewriterInitialDelayMs + brandTitle.length * typewriterCharMs,
        [typewriterInitialDelayMs, brandTitle.length, typewriterCharMs],
    );

    useEffect(() => {
        if (loading || error || !imageUrl) {
            setShowPlayButton(false);
            return undefined;
        }

        setShowPlayButton(false);
        const timer = window.setTimeout(() => setShowPlayButton(true), typewriterCompleteMs);
        return () => window.clearTimeout(timer);
    }, [loading, error, imageUrl, typewriterCompleteMs]);
    useEffect(() => {
        if (loading || error || !imageUrl || !scene) return undefined;

        finishTimerRef.current = window.setTimeout(() => {
            const maxPlays = scene?.maxReloads ?? 3;
            const storageKey = `sliding-animation-plays-${scene?.slug ?? scene?.id ?? "default"}`;
            const playCount = Number(sessionStorage.getItem(storageKey) ?? 0);
            const nextCount = playCount + 1;
            sessionStorage.setItem(storageKey, String(nextCount));

            if (nextCount >= maxPlays) {
                return;
            }

            window.location.href = appendScreenshotLang(animationPath(scene.slug), lang);
        }, brandDurationMs);

        return () => {
            if (finishTimerRef.current) {
                window.clearTimeout(finishTimerRef.current);
                finishTimerRef.current = null;
            }
        };
    }, [loading, error, imageUrl, scene, lang, brandDurationMs]);

    if (isScreenshotSceneExpired(slug)) {
        return <ScreenshotExpiredPage />;
    }

    if (!scene) {
        return (
            <Box sx={{ ...phoneShellSx, p: 2 }}>
                <Typography color="error" sx={{ mb: 1 }}>
                    {t("mobileScreenshot.common.unknown-animation", { slug })}
                </Typography>
                <Link component={RouterLink} to={SCREENSHOTS_MAIN_PATH} sx={{ color: slidingScreenshotAccent }}>
                    {t("mobileScreenshot.common.back-to-index")}
                </Link>
            </Box>
        );
    }

    if (loading) {
        return (
            <Box sx={{ ...phoneShellSx, alignItems: "center", justifyContent: "center" }}>
                <CircularProgress size={32} sx={{ color: slidingScreenshotAccent }} />
            </Box>
        );
    }

    if (error || !template || !imageUrl || grid <= 0) {
        return (
            <Box sx={{ ...phoneShellSx, p: 2 }}>
                <Typography color="error" sx={{ mb: 1 }}>
                    {error ?? t("mobileScreenshot.common.could-not-load")}
                </Typography>
                <Link component={RouterLink} to={SCREENSHOTS_MAIN_PATH} sx={{ color: slidingScreenshotAccent }}>
                    {t("mobileScreenshot.common.back-to-index")}
                </Link>
            </Box>
        );
    }

    return (
        <Box
            sx={{
                position: "fixed",
                inset: 0,
                width: "100%",
                height: "100dvh",
                overflow: "hidden",
                bgcolor: "#0a0a0a",
            }}
        >
            <Box
                sx={{
                    position: "absolute",
                    inset: 0,
                    overflow: "hidden",
                }}
            >
                <Box
                    component="img"
                    src={imageUrl}
                    alt=""
                    draggable={false}
                    referrerPolicy="no-referrer"
                    sx={{
                        width: "100%",
                        height: "100%",
                        minHeight: "100dvh",
                        objectFit: "cover",
                        objectPosition: "center",
                        opacity: 0.7,
                        display: "block",
                    }}
                />
            </Box>

            <Box
                sx={{
                    position: "absolute",
                    inset: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    zIndex: 10,
                    pointerEvents: "none",
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        px: 2,
                        ...screenshotShellFontSx(lang),
                    }}
                >
                    <AnimationTypewriterTitle
                        variant="overlay"
                        text={brandTitle}
                        charMs={typewriterCharMs}
                        initialDelayMs={typewriterInitialDelayMs}
                    />
                    <Box
                        component="span"
                        role="presentation"
                        sx={{
                            mt: 1.75,
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            minHeight: 36,
                            minWidth: 128,
                            px: 2.5,
                            py: 0.65,
                            borderRadius: 999,
                            bgcolor: "#ffffff",
                            color: "#2D3E40",
                            fontWeight: 700,
                            fontSize: "0.82rem",
                            letterSpacing: "0.06em",
                            textTransform: "uppercase",
                            boxShadow: "0 2px 8px rgba(15, 23, 42, 0.12)",
                            opacity: showPlayButton ? 1 : 0,
                            transform: showPlayButton ? "translateY(0)" : "translateY(6px)",
                            transition: "opacity 0.45s ease, transform 0.45s ease",
                        }}
                    >
                        {t("landing.play-now")}
                    </Box>
                </Box>
            </Box>
        </Box>
    );
}
